import { CrewMark } from '@/components/CrewMark'
import type { Challenge, ChallengeWeek } from '@/domain/workout'
import type { Progress } from '@/domain/session'
import { totalCredits } from '@/domain/session'
import { workoutDuration } from '@/domain/timeline'
import { minutes } from '@/lib/format'
import { hasStarted, opensOnLabel } from '@/lib/weeks'
import { Bonus, Prizes, Rules, SectionHead } from './InfoSections'
import { HistoryLog } from './HistoryLog'
import { PhaseList } from './PhaseList'
import { SessionCounter } from './SessionCounter'
import { WeekPicker } from './WeekPicker'
import { WeekPoster } from './WeekPoster'
import styles from './panel.module.css'

interface PanelScreenProps {
  challenge: Challenge
  progress: Progress
  week: ChallengeWeek
  onSelectWeek: (weekNumber: number) => void
  onStart: () => void
  onBackup: () => void
}

/** El panel: la pantalla de inicio con el reto, el progreso y el entreno. */
export function PanelScreen({
  challenge,
  progress,
  week,
  onSelectWeek,
  onStart,
  onBackup,
}: PanelScreenProps) {
  const duration = minutes(workoutDuration(week.workout))
  // Las semanas futuras se anuncian, pero su entreno no se enseña ni se abre.
  const open = hasStarted(week)

  return (
    <div className={styles.wrap}>
      <div className={styles.top}>
        <CrewMark className={styles.topMark} title="Turia Fit Crew" />
        <div className={styles.topText}>
          <strong>Turia Fit Crew</strong>
          <span>Est · 2026 · València</span>
        </div>
      </div>

      <WeekPoster week={week} totalWeeks={challenge.weeks.length} />

      <SessionCounter
        credits={totalCredits(progress)}
        target={challenge.targetSessions}
        sessionsPerWeek={challenge.sessionsPerWeek}
        weeks={challenge.weeks.length}
      />

      {open ? (
        <button type="button" className={styles.cta} onClick={onStart}>
          Empezar entreno
        </button>
      ) : (
        <div className={styles.locked}>Se abre el {opensOnLabel(week)}</div>
      )}

      <WeekPicker
        challenge={challenge}
        progress={progress}
        activeWeek={week.number}
        onSelect={onSelectWeek}
      />

      <section className={styles.section}>
        <SectionHead title="El entreno" note={open ? `±${duration} min` : 'Cerrado'} />
        {open ? (
          <PhaseList workout={week.workout} />
        ) : (
          <p className={styles.lockedNote}>
            El entreno de esta semana se ve el {opensOnLabel(week)}. Mientras tanto, a por el de la semana en
            curso.
          </p>
        )}
      </section>

      <Rules challenge={challenge} />
      <Bonus challenge={challenge} progress={progress} />
      <Prizes challenge={challenge} progress={progress} />
      <HistoryLog progress={progress} />

      <footer className={styles.footer}>
        <CrewMark className={styles.footerMark} />
        <p>
          {challenge.footer.map((line, index) => (
            <span key={index}>
              {line}
              {index < challenge.footer.length - 1 && <br />}
            </span>
          ))}
        </p>
        <div className={styles.footerActions}>
          <button type="button" className={styles.link} onClick={onBackup}>
            Copia de seguridad
          </button>
        </div>
      </footer>
    </div>
  )
}
